import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser'
import { IonActionSheet } from '@ionic/react'
import { close, link, stopwatch } from 'ionicons/icons'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { logEvent } from '../../../analytics/utils/firebaseUtils'
import { AppContext } from '../../../contexts/AppContext'
import { trpc } from '../../../modules/trpc'
import { getInterventionIdsFromHvac } from '../../../utils/getInterventionIdsWithoutDuplicates'
import PageWrapper from '../../../wrappers/pageWrapper/PageWrapper'
import InterventionList from '../electricCar/components/Interventions'
import { HvacContext } from './HvacContext'
import Controls from './components/Controls'
import HeatingControls from './components/HeatingControls'
import SmartHeatingToggle from './components/SmartHeatingToggle'
import TemperatureControls from './components/TemperatureControls'

const HvacDetail: React.FC = () => {
	const { t } = useTranslation('pages_hvac')
	const { isNativePlatform } = useContext(AppContext)
	const history = useHistory()

	const [menuOpen, setMenuOpen] = useState(false)
	const { unlinkHvac, hvac } = useContext(HvacContext)

	const [interventionIds, setInterventionIds] = useState<string[]>([])

	useEffect(() => {
		if (!hvac) return
		const interventions = getInterventionIdsFromHvac(hvac)
		const allIds = [
			...(interventions.setPermanentHold || []),
			...(interventions.setFollowSchedule || []),
		]
		setInterventionIds(allIds)
	}, [hvac])

	const redirectUri = isNativePlatform
		? 'https://vihreaenergia.fi/'
		: window.location.origin + '/devices'

	const utils = trpc.useContext()

	const { refetch: relinkHvac } = trpc.eNode.user.relink.useQuery(
		{ assetId: hvac?.id ?? '', language: 'browser', redirectUri },
		{
			enabled: false,
			onSuccess: data => {
				if (!data?.linkUrl) return

				if (isNativePlatform) {
					// Open in InAppBrowser for native platforms
					const browser = InAppBrowser.create(data.linkUrl, '_blank', 'location=yes')

					browser.on('loadstart').subscribe((event: any) => {
						const isRedirected =
							event.url.includes('vihreaenergia.fi') || event.url.includes(window.location.origin)

						if (!isRedirected) return

						browser.close()
						utils.objects.getEnergyReport.invalidate()
						utils.eNode.hvac.invalidate()
						history.goBack()
					})
				} else {
					// Open in new tab/window for web platforms
					window.open(data.linkUrl, '_blank')
				}
			},

			onError: error => {
				console.error('Error relinking Hvac', error)
			},
		}
	)
	return (
		<PageWrapper
			page='hvac'
			appBarOptions={{
				beta: true,
				ellipsisFunction: () => setMenuOpen(true),
				navigationType: 'back',
			}}
		>
			<InterventionList interventionIds={interventionIds} />
			<div className='temperature-display z-0 -mx-4 sm:-mx-6 md:-mx-8'>
				<HeatingControls />
			</div>

			<div className='flex flex-col gap-6 bg-white dark:bg-[#0d2d30] border border-gray-200 dark:border-[#155f65] p-6 rounded-xl shadow-sm'>
				<Controls />
				<TemperatureControls />
				<SmartHeatingToggle />
			</div>

			<IonActionSheet
				isOpen={menuOpen}
				onDidDismiss={() => setMenuOpen(false)}
				buttons={[
					{
						text: t('unlinkHvac'),
						icon: stopwatch,
						handler: () => {
							logEvent('click-hvac-unlink')
							if (hvac) unlinkHvac(hvac)
						},
					},
					{
						text: t('peruuta', { ns: 'common' }),
						role: 'cancel',
						icon: close,
						handler: () => {
							logEvent('click-hvac-cancel')
						},
					},
					{
						text: t('relinkHvac'),
						icon: link,
						handler: () => {
							logEvent('click-hvac-relink')
							if (!hvac?.id) return
							relinkHvac()
						},
					},
				]}
			/>
		</PageWrapper>
	)
}

export default HvacDetail
